/**
 * Справочные оверлеи GeoServer на тактической карте (view-only, каталог платформы).
 */
import { useQuery } from "@tanstack/react-query";
import { SectionHelpTooltip } from "@/components/shared/SectionHelpTooltip";
import { Label } from "@/components/ui/label";
import { Slider } from "@/components/ui/slider";
import { Switch } from "@/components/ui/switch";
import { useMapUi } from "@/contexts/MapUiContext";
import { usePermissions } from "@/hooks/useAuthV2";
import { hasToken } from "@/lib/api-client-v2";
import { referenceLayersApi } from "@/services/reference-layers-api.service";

export function TacticalReferenceLayersPanel() {
  const { referenceLayerUi, setReferenceLayerEnabled, setReferenceLayerOpacity } =
    useMapUi();
  const { data: permissions = [] } = usePermissions();
  const canView =
    permissions.includes("admin.access") ||
    permissions.includes("reference_layers.view");

  const { data: layers = [], isLoading } = useQuery({
    queryKey: ["reference-layers", "catalog", "tactical"],
    queryFn: () => referenceLayersApi.listForUser(),
    enabled: hasToken() && canView,
  });

  if (!canView) return null;
  if (!isLoading && layers.length === 0) return null;

  return (
    <div className="space-y-2 border-t pt-2">
      <div className="flex items-center gap-1 px-1">
        <p className="text-xs font-medium text-muted-foreground">Справочные слои</p>
        <SectionHelpTooltip label="Справка: справочные оверлеи">
          <p>
            Подложки каталога GeoServer платформы (ортофото, карты и т.п.) поверх доски —
            только просмотр.
          </p>
          <ul className="list-disc space-y-1 pl-4">
            <li>Те же слои и ACL, что на карте ОИ (`reference_layers.view`).</li>
            <li>Вкл/выкл и прозрачность — локально для сессии карты.</li>
            <li>Клик по оверлею не создаёт фичу COP.</li>
            <li>Импорт KML/Shapefile — отдельно в «Обмен» и добавляет фичи доски.</li>
          </ul>
        </SectionHelpTooltip>
      </div>

      {isLoading && (
        <p className="px-1 text-[10px] text-muted-foreground">Загрузка каталога…</p>
      )}

      <div className="space-y-2 px-1">
        {layers.map((layer) => {
          const ui = referenceLayerUi[layer.id];
          const enabled = ui?.enabled ?? false;
          const opacity = ui?.opacity ?? layer.default_opacity;
          return (
            <div key={layer.id} className="space-y-1.5">
              <div className="flex items-center justify-between gap-2">
                <Label className="cursor-pointer text-xs leading-snug">{layer.title}</Label>
                <Switch
                  checked={enabled}
                  onCheckedChange={(v) =>
                    setReferenceLayerEnabled(layer.id, v, layer.default_opacity)
                  }
                  aria-label={`Слой ${layer.title}`}
                />
              </div>
              {enabled && (
                <div className="flex items-center gap-2 pl-0.5">
                  <span className="w-14 shrink-0 text-[10px] text-muted-foreground">
                    Прозр.
                  </span>
                  <Slider
                    min={0}
                    max={100}
                    step={5}
                    value={[Math.round(opacity * 100)]}
                    onValueChange={([v]) =>
                      setReferenceLayerOpacity(layer.id, (v ?? 0) / 100)
                    }
                    className="flex-1"
                    aria-label={`Прозрачность ${layer.title}`}
                  />
                  <span className="w-7 text-right text-[10px] text-muted-foreground">
                    {Math.round(opacity * 100)}%
                  </span>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
